//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { createSelector } from 'reselect';

import { TIKTOK_API_DATE_FORMAT } from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';
import { IData as IChartData } from '@insights/components/common/trend-chart/InsightsDualChart';
import { ICuralateStore } from '@models/AppState';
import DateString from '@models/DateString';
import { ReadableNumberSignOption } from '@utils/readableNumber';

const getTikTokAudience = (state: ICuralateStore) => state.tiktok.audience;

export const getTikTokAudienceSummaryStats = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.summaryStats,
);

export const getTikTokAudienceIsFetchingSummaryStats = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.flags.isFetchingSummaryStats,
);

export const getTikTokAudienceDailyStats = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.dailyStats,
);

export const getTikTokAudienceIsFetchingDailyStats = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.flags.isFetchingDailyStats,
);

export const getTikTokAudienceEarliestMetricsDate = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.earliestMetricsDate,
);

export const getTikTokAudienceHasFetchedEarliestMetricsDate = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.flags.hasFetchedEarliestMetricsDate,
);

export const getTikTokAudienceDateRange = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.dateRange,
);

export const getTikTokAudienceDailyStatsChartData = createSelector(
  getTikTokAudienceDailyStats,
  (dailyStats) =>
    dailyStats.reduce(
      (data: IChartData, stat) => {
        const { categories, columnSeriesData, lineSeriesData } = data;
        categories.push(
          DateString.fromString(stat.date, TIKTOK_API_DATE_FORMAT),
        );
        columnSeriesData?.[0]?.series?.push(
          stat.metrics.changeInFollowers ?? 0,
        ); // using 0 because non-numeric items appear as a break in the chart
        lineSeriesData?.[0]?.series?.push(stat.metrics.followersCount ?? 0);
        return data;
      },
      {
        categories: [],
        columnSeriesData: [
          {
            label: 'Change from Previous Day',
            series: [],
            legendLabel: 'Change in Followers',
            signOption: ReadableNumberSignOption.BOTH,
            yAxisPlacement: 'right',
          },
        ],
        lineSeriesData: [
          {
            label: 'Follower Count',
            series: [],
            legendLabel: 'Follower Count',
            yAxisPlacement: 'left',
          },
        ],
      },
    ),
);

export const getTikTokOnlineFollowersHeatmap = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.onlineFollowersHeatmap,
);

export const getIsFetchingOnlineFollowersHeatmap = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.flags.isFetchingOnlineFollowersHeatmap,
);

export const getFetchOnlineFollowersHeatMapError = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.errors.fetchOnlineFollowersHeatMapError,
);

export const getTikTokBestTimesToPost = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.bestTimesToPost,
);

export const getIsFetchingBestTimesToPost = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.flags.isFetchingBestTimesToPost,
);

export const getFetchBestTimesToPostError = createSelector(
  getTikTokAudience,
  (tiktokAudience) => tiktokAudience.errors.fetchBestTimesToPostError,
);
